import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Moon, Sun, X, RefreshCw, Plus } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import QuickConvert from "../components/QuickConvert";
import { canvasToICO } from "../utils/imageConverters";

export default function ConvertHeicToIco() {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Convert HEIC to ICO - KayTool";
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif')
    );
    if (files.length > 0) processImages(files);
    else toast.error("Please drop HEIC files only");
  };

  const processImages = async (files) => {
    setIsProcessing(true);
    setProgress(0);
    const newImages = [];

    for (let i = 0; i < files.length; i++) {
      try {
        setProgress(Math.round((i / files.length) * 100));
        const file = files[i];
        
        const blob = await heic2any({ blob: file, toType: "image/png" });
        const pngBlob = Array.isArray(blob) ? blob[0] : blob;
        
        const img = new Image();
        const url = URL.createObjectURL(pngBlob);
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });
        
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        
        const icoBlob = canvasToICO(canvas, [32, 64, 128]);
        const convertedFile = new File([icoBlob], file.name.replace(/\.heic$/i, '.ico').replace(/\.heif$/i, '.ico'), { type: 'image/x-icon' });
        
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalSize: file.size,
          convertedFile,
          convertedSize: convertedFile.size,
          preview: URL.createObjectURL(pngBlob),
          dimensions: { width: img.width, height: img.height }
        });
      } catch (error) {
        console.error(`Error converting ${files[i].name}:`, error);
        toast.error(`Failed to convert ${files[i].name}`);
      }
    }

    setProgress(100);
    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    if (newImages.length > 0) toast.success(`Converted ${newImages.length} image(s) to ICO!`);
  };

  const downloadImage = (image) => saveAs(new Blob([image.convertedFile]), image.convertedFile.name);

  const downloadAllAsZip = async () => {
    const zip = new JSZip();
    for (const img of images) {
      zip.file(img.convertedFile.name, img.convertedFile);
    }
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "heic-to-ico-converted.zip");
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-rose-50 to-pink-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 dark:text-white">HEIC to ICO</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">by KayTool</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <QuickConvert />
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 border-rose-200 text-rose-600 bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:bg-rose-900/20">
              <RefreshCw className="w-3 h-3" /> HEIC → ICO
            </Badge>
            <Button variant="ghost" size="icon" onClick={() => setDarkMode(prev => !prev)}>
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-3">Convert HEIC to ICO</h2>
          <p className="text-slate-600 dark:text-slate-300">Convert iPhone HEIC photos to ICO favicon format. Free and private.</p>
        </div>

        <div
          className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer
            ${isDragging ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/20' : 'border-slate-300 dark:border-slate-600 hover:border-rose-400 bg-white dark:bg-slate-800'}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".heic,.heif" multiple onChange={(e) => { processImages(Array.from(e.target.files)); e.target.value = ''; }} className="hidden" />
          <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-rose-600' : 'text-slate-400'}`} />
          <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Drop HEIC files here or click to upload</p>
          <p className="text-sm text-slate-500 mt-1">Multiple files supported</p>
        </div>

        {isProcessing && (
          <div className="mt-6">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 text-center">Converting... {progress}%</p>
          </div>
        )}

        {images.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Converted Images ({images.length})</h3>
              <div className="flex gap-2">
                <input ref={addMoreInputRef} type="file" accept=".heic,.heif" multiple onChange={(e) => { processImages(Array.from(e.target.files)); e.target.value = ''; }} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => addMoreInputRef.current?.click()} className="text-green-600 border-green-200">
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
                <Button variant="outline" size="sm" onClick={() => setImages([])} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                {images.length > 1 && (
                  <Button size="sm" onClick={downloadAllAsZip} className="bg-rose-600 hover:bg-rose-700">
                    <Download className="w-4 h-4 mr-1" /> Download All (ZIP)
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden bg-white dark:bg-slate-800">
                  <div className="aspect-video relative bg-slate-100 dark:bg-slate-700">
                    <img src={image.preview} alt={image.name} className="w-full h-full object-contain" />
                    <button onClick={() => setImages(prev => prev.filter(i => i.id !== image.id))} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600">
                      <X className="w-4 h-4" />
                    </button>
                    <Badge className="absolute bottom-2 left-2 bg-rose-600">ICO</Badge>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate text-sm">{image.name}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <span>{formatSize(image.originalSize)}</span>
                      <span>→</span>
                      <span className="font-medium text-rose-600">{formatSize(image.convertedSize)}</span>
                    </div>
                    <Button onClick={() => downloadImage(image)} className="w-full mt-3 bg-rose-600 hover:bg-rose-700" size="sm">
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2025 KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>
    </div>
  );
}
